import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

const GAP = 4;

interface Options {
  /** Fixed menu width in px — also the clamp used to keep it on screen. */
  width: number;
  /** Which trigger edge the menu lines up with. */
  align?: 'left' | 'right';
  maxHeight?: number;
}

/**
 * Dropdown plumbing for a button-anchored menu: open state, outside-click and
 * Escape dismissal, and fixed-position placement measured off the trigger.
 *
 * Placement is `position: fixed` and the caller portals the menu to <body>,
 * because these menus live inside cards with `overflow: hidden` that would
 * otherwise clip them. Flips above the trigger when there's more room there.
 */
export function useAnchoredMenu({ width, align = 'right', maxHeight = 288 }: Options) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom - GAP * 2;
      const above = r.top - GAP * 2;
      const dropUp = below < Math.min(maxHeight, above);
      const height = Math.max(120, Math.min(maxHeight, dropUp ? above : below));
      const left = Math.max(
        GAP,
        Math.min(window.innerWidth - width - GAP, align === 'left' ? r.left : r.right - width),
      );
      setStyle({
        position: 'fixed',
        top: dropUp ? r.top - height - GAP : r.bottom + GAP,
        left,
        width,
        maxHeight: height,
        zIndex: 70,
      });
    };
    place();
    // Capture phase so scrolling any ancestor container re-places the menu.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, width, align, maxHeight]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return { open, setOpen, triggerRef, menuRef, style };
}
